import { keepPreviousData } from "@tanstack/react-query";
import { TemplateKey } from "PFApp/constants/templates";
import { useAuditRoles } from "PFCore/hooks/queries/roles/use_audit_roles";
import { useRoles } from "PFCore/hooks/queries/roles/use_roles";
import { FetchAuditRolesResponse, FetchRolesParams, FetchRolesResponse } from "PFCore/services/roles";
import { useMemo, useState } from "react";

import { useActivityShowContext } from "../context/activity_show_context";
import { useCurrentActivity } from "./use_current_activity";

const DEFAULT_ROLES = {
  entries: [],
  meta: { total: 0, page: 1, perPage: 25, totalPages: 1 }
};

type UseActivityPageRolesReturn = {
  roles: FetchRolesResponse | FetchAuditRolesResponse;
  changePage: (params: FetchRolesParams) => void;
  isLoading: boolean;
  isFetching: boolean;
};

export const useActivityPageRoles = (): UseActivityPageRolesReturn => {
  const activity = useCurrentActivity();
  const { isEngagement, roleKey } = useActivityShowContext();

  const [params, setParams] = useState<FetchRolesParams>({
    page: 1,
    perPage: 25
  });

  const payload = useMemo(
    () => ({
      ...params,
      filters: {
        fields: {
          parentActivityId: activity.id,
          state: ["draft", "new", "complete"]
        }
      }
    }),
    [params, activity.id]
  );

  const isKindRole = roleKey === TemplateKey.Role;

  const {
    data: roles,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles
  } = useRoles(
    { withoutSegmentation: true, ...payload },
    {
      placeholderData: keepPreviousData,
      enabled: isEngagement && isKindRole
    }
  );

  const {
    data: auditRoles,
    isLoading: isLoadingAuditRoles,
    isFetching: isFetchingAuditRoles
  } = useAuditRoles({
    params: payload,
    options: {
      placeholderData: keepPreviousData,
      enabled: isEngagement && !isKindRole
    }
  });

  const changePage = (params: FetchRolesParams) => {
    setParams(params);
  };

  return {
    roles: (isKindRole ? roles : auditRoles) || DEFAULT_ROLES,
    changePage,
    isLoading: isLoadingRoles || isLoadingAuditRoles,
    isFetching: isFetchingRoles || isFetchingAuditRoles
  };
};
